import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { downloadFile } from '../api';

interface Cycle { id: number; name: string; status: string; year: number; }
interface DeptStat { department_id: number; department_name: string; total: number; submitted: number; percent: number; }
interface UserDetail {
  user_id: number; full_name: string; email: string; org_layer: string;
  department_id: number; department_name: string; manager_id: number; manager_name: string;
  total_assignments: number; submitted: number; pending: number; draft: number;
  assignments: { id: number; template_name: string; subject_name: string; status: string; submitted_at: string }[];
}
interface Dashboard { cycle: any; total_assignments: number; submitted: number; percent: number; by_department: DeptStat[]; users: UserDetail[]; surveys?: { id: number; name: string; submitted: number; total: number }[]; }

const layerLabel: Record<string, string> = { employee: 'עובד', manager: 'מנהל', hq: 'מטה', ceo: 'מנהלת הרשות' };
const statusLabel: Record<string, string> = { pending: 'טרם התחיל', draft: 'טיוטה', submitted: 'הוגש' };
const statusClass: Record<string, string> = { pending: 'badge-pending', draft: 'badge-draft', submitted: 'badge-submitted', active: 'badge-active', closed: 'badge-closed', draft_cycle: 'badge-pending' };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [selectedCycle, setSelectedCycle] = useState<number | null>(null);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [filterDept, setFilterDept] = useState('');
  const [filterLayer, setFilterLayer] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedUser, setExpandedUser] = useState<number | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [namedVersion, setNamedVersion] = useState(false); // false = anonymised documents, true = with respondent names
  const anonQ = () => `?anonymous=${namedVersion ? 0 : 1}`;
  const verSuffix = () => (namedVersion ? ' (עם שמות)' : '');

  useEffect(() => {
    api.get('/cycles/').then(({ data }) => {
      setCycles(data);
      const active = data.find((c: Cycle) => c.status === 'active');
      if (active) setSelectedCycle(active.id);
      else if (data.length) setSelectedCycle(data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedCycle) return;
    setLoading(true);
    api.get(`/admin/dashboard/${selectedCycle}`)
      .then(({ data }) => setDashboard(data))
      .finally(() => setLoading(false));
  }, [selectedCycle]);

  const handleDownload = async (type: 'employee' | 'manager', userId: number, name: string) => {
    const key = `${type}-${userId}`;
    setDownloading(key);
    try {
      await downloadFile(`/exports/${type}/${userId}/cycle/${selectedCycle}${anonQ()}`, `${name}${verSuffix()}.docx`);
    } catch { } finally {
      setDownloading(null);
    }
  };

  const handleDepartmentZip = async (deptId: number, deptName: string) => {
    const key = `dept-${deptId}`;
    setDownloading(key);
    try { await downloadFile(`/exports/department/${deptId}/cycle/${selectedCycle}${anonQ()}`, `${deptName}${verSuffix()}.zip`); }
    catch { } finally { setDownloading(null); }
  };

  const handleAllDocsZip = async () => {
    setDownloading('all-docs');
    try { await downloadFile(`/exports/departments/cycle/${selectedCycle}${anonQ()}`, `מסמכי_משוב_${cycles.find(c => c.id === selectedCycle)?.year}${verSuffix()}.zip`); }
    catch { } finally { setDownloading(null); }
  };

  const handleExportExcel = async () => {
    await downloadFile(`/exports/excel/${selectedCycle}`, `כל_התשובות_${cycles.find(c => c.id === selectedCycle)?.year}.xlsx`);
  };

  const filteredUsers = dashboard?.users.filter(u => {
    if (filterDept && u.department_id?.toString() !== filterDept) return false;
    if (filterLayer && u.org_layer !== filterLayer) return false;
    if (filterStatus === 'complete' && u.submitted < u.total_assignments) return false;
    if (filterStatus === 'incomplete' && u.submitted === u.total_assignments) return false;
    return true;
  }) || [];

  const departments = Array.from(new Set(dashboard?.users.map(u => u.department_name).filter(Boolean) || []));

  return (
    <div className="page">
      <div className="page-header">
        <h1>לוח בקרה מנהל</h1>
        <div className="page-header-actions">
          <select
            className="form-input"
            style={{ width: 'auto', minWidth: '220px' }}
            value={selectedCycle || ''}
            onChange={e => setSelectedCycle(parseInt(e.target.value))}
          >
            {cycles.map(c => (
              <option key={c.id} value={c.id}>{c.name} — {c.status === 'active' ? 'פעיל' : c.status === 'closed' ? 'סגור' : 'טיוטה'}</option>
            ))}
          </select>
          <button className="btn btn-secondary" onClick={() => navigate('/admin/cycles')}>ניהול תהליכים</button>
          <button className="btn btn-secondary" onClick={() => navigate('/admin/users')}>ניהול עובדים</button>
          {dashboard && (
            <>
              <select className="form-input" style={{ width: 'auto' }} value={namedVersion ? 'named' : 'anon'} onChange={e => setNamedVersion(e.target.value === 'named')} title="גרסת מסמכי ה-Word להורדה">
                <option value="anon">Word: גרסה אנונימית (לשיחות המשוב)</option>
                <option value="named">Word: גרסה עם שמות (למנהלת הרשות)</option>
              </select>
              <button className="btn btn-secondary" onClick={handleAllDocsZip} disabled={downloading === 'all-docs'}>
                {downloading === 'all-docs' ? '...' : 'הורד את כל מסמכי ה-Word'}
              </button>
              <button className="btn btn-primary" onClick={handleExportExcel}>
                ⬇ ייצא Excel
              </button>
            </>
          )}
        </div>
      </div>

      {loading && <div className="loading"><div className="spinner" />טוען...</div>}

      {dashboard && !loading && (
        <>
          {/* Stats */}
          <div className="grid-3" style={{ marginBottom: '24px' }}>
            <div className="stat-card">
              <div className="stat-number">{dashboard.percent}%</div>
              <div className="stat-label">הושלמו</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{dashboard.submitted}</div>
              <div className="stat-label">שאלונים שהוגשו</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{dashboard.total_assignments - dashboard.submitted}</div>
              <div className="stat-label">שאלונים שנותרו</div>
            </div>
          </div>

          {dashboard.surveys && dashboard.surveys.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <div className="card-header"><div className="card-title">סקרים ארגוניים</div></div>
              {dashboard.surveys.map(sv => (
                <div key={sv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '6px 0', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{sv.name}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span className="text-muted">{sv.submitted}/{sv.total} הוגשו</span>
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/survey/${sv.id}`)}>תוצאות</button>
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* By department */}
          {dashboard.by_department.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <div className="card-header"><div className="card-title">התקדמות לפי מחלקה</div></div>
              {dashboard.by_department.map(dept => (
                <div key={dept.department_id} style={{ marginBottom: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '14px', gap: '8px' }}>
                    <span style={{ fontWeight: 600 }}>{dept.department_name}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span className="text-muted">{dept.submitted}/{dept.total} ({dept.percent}%)</span>
                      <button className="btn btn-secondary btn-sm" disabled={downloading === `dept-${dept.department_id}`}
                        onClick={() => handleDepartmentZip(dept.department_id, dept.department_name)}>
                        {downloading === `dept-${dept.department_id}` ? '...' : 'הורד מסמכי Word למחלקה'}
                      </button>
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-bar-fill" style={{ width: `${dept.percent}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="filters">
            <select value={filterDept} onChange={e => setFilterDept(e.target.value)}>
              <option value="">כל המחלקות</option>
              {departments.map(d => <option key={d} value={dashboard.users.find(u => u.department_name === d)?.department_id?.toString()}>{d}</option>)}
            </select>
            <select value={filterLayer} onChange={e => setFilterLayer(e.target.value)}>
              <option value="">כל השכבות</option>
              <option value="employee">עובד</option>
              <option value="manager">מנהל</option>
              <option value="hq">מטה</option>
              <option value="ceo">מנהלת הרשות</option>
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">כל הסטטוסים</option>
              <option value="complete">הושלמו</option>
              <option value="incomplete">לא הושלמו</option>
            </select>
            <span className="text-muted" style={{ fontSize: '13px' }}>מציג {filteredUsers.length} עובדים</span>
          </div>

          {/* User table */}
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>שם</th>
                  <th>מחלקה</th>
                  <th>שכבה</th>
                  <th>מנהל</th>
                  <th>התקדמות</th>
                  <th>ייצוא</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => (
                  <React.Fragment key={u.user_id}>
                    <tr
                      onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ fontWeight: 600 }}>{u.full_name}</td>
                      <td>{u.department_name || '—'}</td>
                      <td><span className="badge badge-active" style={{ background: 'var(--gray-100)', color: 'var(--gray-700)' }}>{layerLabel[u.org_layer]}</span></td>
                      <td>{u.manager_name || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div className="progress-bar" style={{ width: '80px' }}>
                            <div className="progress-bar-fill" style={{ width: `${u.total_assignments ? u.submitted / u.total_assignments * 100 : 0}%` }} />
                          </div>
                          <span className="text-sm text-muted">{u.submitted}/{u.total_assignments}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {(
                            <button
                              className="btn btn-secondary btn-sm"
                              title="בחירת מה ייכלל במסמך והוספת הערות לפני ההורדה"
                              onClick={e => { e.stopPropagation(); navigate(`/admin/review/${u.user_id}/${selectedCycle}`); }}
                            >
                              ערוך מסמך
                            </button>
                          )}
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={downloading === `employee-${u.user_id}`}
                            title={namedVersion ? 'הורדת מסמך ה-Word – גרסה עם שמות' : 'הורדת מסמך ה-Word – גרסה אנונימית'}
                            onClick={e => { e.stopPropagation(); handleDownload('employee', u.user_id, u.full_name); }}
                          >
                            {downloading === `employee-${u.user_id}` ? '...' : '⬇ הורדה'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedUser === u.user_id && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--gray-50)', padding: '12px 20px' }}>
                          <div style={{ fontSize: '13px' }}>
                            {u.assignments.map(a => (
                              <div key={a.id} style={{ display: 'flex', gap: '12px', padding: '4px 0', alignItems: 'center' }}>
                                <span style={{ flex: 1 }}>{a.template_name}{a.subject_name ? ` — ${a.subject_name}` : ''}</span>
                                <span className={`badge ${statusClass[a.status]}`}>{statusLabel[a.status]}</span>
                                {a.submitted_at && <span className="text-muted">{new Date(a.submitted_at).toLocaleDateString('he-IL')}</span>}
                                {a.status === 'submitted' && (
                                  <button className="btn btn-secondary btn-sm" disabled={downloading === `reopen-${a.id}`}
                                    title="מחזיר את השאלון למצב טיוטה. התשובות נשמרות והממלא/ת יכול/ה לערוך ולהגיש שוב."
                                    onClick={async e => {
                                      e.stopPropagation();
                                      if (!window.confirm(`לפתוח מחדש את "${a.template_name}" של ${u.full_name}? התשובות יישמרו, והשאלון יחזור למצב טיוטה עד להגשה מחדש.`)) return;
                                      setDownloading(`reopen-${a.id}`);
                                      try { await api.post(`/responses/assignment/${a.id}/reopen`); const { data } = await api.get(`/admin/dashboard/${selectedCycle}`); setDashboard(data); }
                                      catch (err: any) { alert(err.response?.data?.detail || 'שגיאה'); }
                                      finally { setDownloading(null); }
                                    }}>
                                    פתח מחדש לעריכה
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
